/**
 * CRM Shell — bootstrap, sidebar, top bar, hash router.
 *
 * Phase 1 (2026-05-07): минимальный shell. Калькулятор остаётся как есть,
 * sidebar инжектируется через position:fixed без перестройки HTML.
 *
 * Архитектура:
 *   - При загрузке: создаём sidebar+topbar, ставим body.crm-shell-active
 *   - Hash-router: #/<route> → класс body.crm-route-<route>
 *   - На /#/calculator (или пустой hash): показываем существующий калькулятор
 *   - На других роутах: скрываем калькулятор, рендерим placeholder в #crm-section-content
 *
 * Роли (определяются из JWT):
 *   - superadmin: все секции + ссылка на /admin/ (управление всеми орг.)
 *   - admin: все секции + ссылка на /admin/ (управление своей орг.)
 *   - user: все секции, но без ссылки на /admin/
 *
 * @module crm-shell/shell
 */

import { fetchAuth } from './api.js';  // Sprint 20w: shell auth bug fix — auto-refresh cookies
import { cleanupLegacyStorage, clearOrgStorage } from './storage-cleanup.js';  // Sprint 20w follow-up: prevent leaks
import { renderPlaceholder } from './sections/placeholder.js';
import { mountHelpWidget } from './help-widget.js';  // Sprint 23 W6+: floating help (?) FAB
// Sprint 20w perf Phase 3.2: dynamic imports — most sections загружаются по требованию.
// Только clients (default route) остаётся в initial bundle. Vite автоматически
// code-splits их в отдельные chunks → загружаются параллельно при первой нав.
// Уменьшает initial JS bundle на ~150 KB (parsed sections для unauth/login flow).
import { renderClientsSection } from './sections/clients.js';
// Sprint 21 W4: i18n активация (раньше модуль был, но не импортировался)
import { setLocale, t, applyTranslations } from '../i18n/index.js';
// Sprint 23 W1: ensure window.__dtFormat__ available для legacy callers
import '../utils/datetime-format.js';
// Sprint 24: native dialog replacements (i18n-aware)
import { confirmModal } from '/public/js/components/text-prompt-modal.js';

// Sprint 24: local i18n helper — works also before i18n module fully loaded.
// Prefers global window.i18n.t (single source) so re-renders pick up new locale.
const _t = (k, fb) => {
    const v = (window.i18n && typeof window.i18n.t === 'function') ? window.i18n.t(k) : t(k);
    return (v && v !== k) ? v : (fb || k);
};

// ─── Routes config ──────────────────────────────────────────────────────────
// Sprint 20m: i18n keys для sidebar labels — fallback на label если t() не нашёл.
// Format: i18nKey совпадает с keys в public/js/i18n/locales/*.json → nav.<key>
const ROUTES = [
    // Sprint 18: "Сметы" удалён из sidebar — теперь раздел сметы доступен через
    // клиентов (expandable rows + submenu "Все сметы" внутри /#/clients).
    // Route /#/estimates остаётся живым для backward compat (URL прямые ссылки).
    // Sprint 18 UI/UX: icon = Lucide icon name (см. https://lucide.dev/icons)
    { path: 'clients',    label: 'Клиенты',        i18nKey: 'clients',    icon: 'users',           default: true },
    { path: 'calculator', label: 'Калькулятор',    i18nKey: 'calculator', icon: 'calculator', hidden: true }, // deep-link only — кнопка «+ Новая смета» в Клиентах/Сметах покрывает
    { path: 'tours',      label: 'Туры',           i18nKey: 'tours',      icon: 'map',             hidden: true }, // deep-link only: /#/tours/:id
    { path: 'estimates',  label: 'Сметы (все)',    i18nKey: 'estimates',  icon: 'clipboard-list',  adminOnly: true },
    { path: 'suppliers',  label: 'Поставщики',     i18nKey: 'suppliers',  icon: 'building-2' },
    { path: 'partners',   label: 'Партнёры',       i18nKey: 'partners',   icon: 'handshake' },
    { path: 'catalogs',   label: 'Прайсы',         i18nKey: 'catalogs',   icon: 'wallet' },
    { path: 'dashboard',  label: 'Дашборд',        i18nKey: 'dashboard',  icon: 'bar-chart-3' },
    { path: 'tracker',    label: 'Прогресс',       i18nKey: 'progress',   icon: 'check-circle' },
    { path: 'audit-log',  label: 'Аудит',          i18nKey: 'audit',      icon: 'search',          adminOnly: true },
    { path: 'team',       label: 'Команда',        i18nKey: 'team',       icon: 'users-round',     adminOnly: true },
    { path: 'settings',   label: 'Настройки',      i18nKey: 'settings',   icon: 'settings' },
    { path: 'profile',    label: 'Профиль',        i18nKey: 'profile',    icon: 'user',            hidden: true }, // deep-link only — теперь только через user-dropdown
    { path: 'dev-tracker', label: 'Dev Tracker',   i18nKey: 'dev_tracker', icon: 'kanban-square', superadminMgmtOnly: true },
];
const DEFAULT_ROUTE = 'clients';  // Sprint 18: clients стали primary entry point

// Sprint 20w perf Phase 3.2: section loaders с dynamic import().
// Vite разрезает каждый import('./sections/X.js') в отдельный chunk.
// Сразу загружается только clients (default route), остальные lazy.
// Cache: загруженный module держится в памяти (browser ESM cache).
const SECTION_LOADERS = {
    calculator: null, // не нужен — показываем существующий index.html
    clients:    () => renderClientsSection(),
    estimates:  async () => (await import('./sections/estimates.js')).renderEstimatesSection(),
    tours:      async (sub) => (await import('./sections/tour-card.js')).renderTourCardSection(sub),
    suppliers:  async () => (await import('./sections/suppliers.js')).renderSuppliersSection(),
    partners:   async (sub) => (await import('./sections/partners.js')).renderPartnersSection(sub),
    catalogs:   async () => (await import('./sections/catalogs.js')).renderCatalogsSection(),
    dashboard:  async () => (await import('./sections/dashboard.js')).renderDashboardSection(),
    tracker:    async () => (await import('./sections/tracker.js')).renderTrackerSection(),
    'audit-log': async () => (await import('./sections/audit-log.js')).renderAuditLogSection(),
    team:       async () => (await import('./sections/team.js')).renderTeamSection(),
    settings:   async () => (await import('./sections/settings.js')).renderSettingsSection(),
    profile:    async () => (await import('./sections/profile.js')).renderProfileSection(),
    'dev-tracker': async () => (await import('./sections/dev-tracker.js')).renderDevTrackerSection(),
};

// ─── State ──────────────────────────────────────────────────────────────────
let state = {
    user: null,
    currentRoute: null,
};

// ─── DOM-инжекция ───────────────────────────────────────────────────────────

/**
 * Sidebar HTML.
 * Возвращает строку для innerHTML контейнера.
 */
function renderSidebar() {
    // Sprint 20w fix: 'owner' (Sprint 20 customer role) must have admin-level access in their org —
    // see /admin/ (Команда, Тарифы, Mailcow) and adminOnly routes (Аудит, Сметы all, Dev Tracker).
    const isAdmin = state.user?.role === 'admin'
        || state.user?.role === 'owner'
        || state.user?.role === 'superadmin';
    const isSuperadmin = state.user?.role === 'superadmin';
    // B014 fix: Dev Tracker доступен superadmin'у в любой org (включая sandbox).
    // Раньше был tied к management — superadmin терял access после switch в sandbox.
    const isSuperadminMgmt = isSuperadmin;

    // Sprint 20m / 21 W4: i18n integration. t() impl возвращает key если не нашёл —
    // safe-fallback на route.label через `||`.
    // (Раньше через window.i18n — теперь native ESM import.)

    const navItems = ROUTES
        .filter(r => !r.hidden)  // Sprint 19c: hidden routes (deep-link only)
        .filter(r => !r.adminOnly || isAdmin)  // Sprint 14 D.6: audit-log только для admin/owner/superadmin
        .filter(r => !r.superadminMgmtOnly || isSuperadminMgmt)  // Sprint 20w: Dev Tracker — Magellania-staff only
        .map(r => {
            // Try i18n first; fallback to hardcoded label если ключ возвращает себя
            const i18nLabel = r.i18nKey ? t(`nav.${r.i18nKey}`) : r.label;
            const label = (i18nLabel && i18nLabel !== `nav.${r.i18nKey}`) ? i18nLabel : r.label;
            return `
            <a class="crm-nav-item" data-route="${r.path}" data-i18n="nav.${r.i18nKey || ''}" href="#/${r.path}" title="${label}">
                <span class="nav-icon"><i data-lucide="${r.icon}" style="width:18px;height:18px"></i></span>
                <span class="nav-label">${label}</span>
            </a>
        `;
        }).join('');

    let adminLink = '';
    if (isAdmin) {
        const label = isSuperadmin
            ? _t('nav.admin_panel', 'Админ-панель')
            : _t('nav.my_org', 'Моя организация');
        adminLink = `
            <a class="crm-nav-item" href="/admin/" title="${label}" style="border-top: 1px solid rgba(255,255,255,0.08); margin-top: 8px;">
                <span class="nav-icon"><i data-lucide="shield" style="width:18px;height:18px"></i></span>
                <span class="nav-label">${label}</span>
            </a>
        `;
    }

    // Sprint 24 W2: помощь / документация. Внешний линк на support.magellania.net.
    // Локально / на staging читается DOCS_BASE_URL — может указывать на staging-копию.
    const helpLabel = _t('shell.help_docs', 'Помощь и документация');
    const docsBase = (window.MAGELLANIA_DOCS_URL || 'https://support.magellania.net').replace(/\/$/, '');
    const helpLink = `
        <a class="crm-nav-item" href="${docsBase}/" target="_blank" rel="noopener noreferrer"
           title="${helpLabel}" style="margin-top: 4px;">
            <span class="nav-icon"><i data-lucide="life-buoy" style="width:18px;height:18px"></i></span>
            <span class="nav-label">${helpLabel}</span>
            <span style="margin-left:auto;font-size:10px;opacity:0.55;">↗</span>
        </a>
    `;

    return `
        <div id="crm-sidebar-header">
            <span id="crm-sidebar-logo">MAGELLANIA CRM</span>
            <button id="crm-sidebar-toggle" title="${_t('shell.sidebar.toggle', 'Свернуть/развернуть')}">☰</button>
        </div>
        <nav id="crm-sidebar-nav">
            ${navItems}
            ${helpLink}
            ${adminLink}
            ${helpLink}
        </nav>
        <!-- Version label — ТОЛЬКО для superadmin (debug info) -->
        ${state.user?.role === 'superadmin'
            ? `<div id="crm-sidebar-version">v${(window.VERSION?.app) || '2.5.0'}</div>`
            : ''}
        <!-- User block (Sprint 20w UX): avatar + name + plan + dropdown trigger -->
        ${renderSidebarUserBlock()}
    `;
}

/**
 * Sprint 20w UX: user block в footer sidebar — avatar + name + plan + dropdown.
 * Dropdown содержит Профиль + Выйти (заменяет topbar user-info + logout).
 */
function renderSidebarUserBlock() {
    if (!state.user) return '';
    const userEmail = state.user.email || '';
    const displayName = state.user.full_name
        || state.user.username
        || (userEmail ? userEmail.split('@')[0] : '—');
    const initials = displayName
        .split(/\s+/).filter(Boolean).slice(0, 2)
        .map(s => s[0]?.toUpperCase() || '').join('') || '?';

    // Plan label (Sprint 24 i18n)
    const plan = state.user.is_management === 1
        ? _t('shell.user.plan_management', 'Платформа Magellania')
        : (state.user.is_sandbox === 1
            ? _t('shell.user.plan_sandbox', 'Sandbox')
            : (state.user.organization_name || _t('shell.user.plan_organization', 'Организация')));

    return `
        <div id="crm-sidebar-user-block">
            <button id="crm-sidebar-user-trigger" type="button" title="${_t('shell.user.menu_trigger_title', 'Профиль / Организации / Выйти')}">
                <span class="crm-user-avatar">${escapeHtml(initials)}</span>
                <span class="crm-user-meta">
                    <span class="crm-user-name">${escapeHtml(displayName)}</span>
                    <span class="crm-user-plan">${escapeHtml(plan)}</span>
                </span>
                <i data-lucide="chevrons-up-down" class="crm-user-caret"></i>
            </button>
            <div id="crm-sidebar-user-menu" style="display:none;">
                <!-- Orgs section — заполняется лениво при первом открытии -->
                <div id="crm-user-menu-orgs" class="crm-user-menu-section"></div>
                <!-- Sandbox toggle section — заполняется лениво при первом открытии -->
                <div id="crm-user-menu-sandbox" class="crm-user-menu-section"></div>
                <!-- Static actions -->
                <div class="crm-user-menu-section crm-user-menu-actions">
                    <button type="button" data-action="goto-profile">
                        <i data-lucide="user" style="width:14px;height:14px"></i>
                        <span>${_t('nav.profile', 'Профиль')}</span>
                    </button>
                    <button type="button" data-action="open-docs">
                        <i data-lucide="book-open" style="width:14px;height:14px"></i>
                        <span>${_t('shell.user.docs', 'Документация ↗')}</span>
                    </button>
                    <button type="button" data-action="logout">
                        <i data-lucide="log-out" style="width:14px;height:14px"></i>
                        <span>${_t('nav.logout', 'Выйти')}</span>
                    </button>
                </div>
            </div>
        </div>
    `;
}

/**
 * Top bar HTML.
 */
function renderTopbar() {
    const userEmail = state.user?.email || '';
    // Имя для отображения: full_name → username → email (до @) → '—'
    const displayName = state.user?.full_name
        || state.user?.username
        || (userEmail ? userEmail.split('@')[0] : '—');
    const orgName = state.user?.organization_name || state.user?.organization_id || '—';
    const role = state.user?.role || 'user';
    const isManagement = state.user?.is_management === 1;
    const isSandbox = state.user?.is_sandbox === 1;

    // Аватар-инициалы (до 2 букв)
    const initials = displayName
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(s => s[0]?.toUpperCase() || '')
        .join('') || '?';

    // Sprint 20w UX: org-switcher переехал в sidebar user-dropdown.
    // В topbar остаётся ТОЛЬКО sandbox badge + route + burger.
    const sandboxTitle = _t('shell.sandbox.badge_title', 'Sandbox — данные тестовые, ничего реального');
    const sandboxLabel = _t('shell.sandbox.badge', 'Sandbox');
    return `
        <button id="crm-burger-btn" title="${_t('shell.topbar.menu', 'Меню')}">☰</button>
        <div id="crm-topbar-route">…</div>
        <div id="crm-topbar-actions">
            ${isSandbox ? `<span id="crm-sandbox-badge" title="${sandboxTitle}" style="background:#fef9c3;color:#854d0e;padding:4px 10px;border-radius:6px;font-size:11px;font-weight:600;cursor:help;letter-spacing:0.5px;text-transform:uppercase;">${sandboxLabel}</span>` : ''}
        </div>
    `;
}

function escapeHtml(s) {
    return String(s == null ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/**
 * Создаёт sidebar / topbar / section-content контейнеры в DOM.
 * Вызывается один раз при инициализации.
 */
function injectShellDOM() {
    // 1. Sidebar
    let sidebar = document.getElementById('crm-sidebar');
    if (!sidebar) {
        sidebar = document.createElement('aside');
        sidebar.id = 'crm-sidebar';
        sidebar.innerHTML = renderSidebar();
        document.body.insertBefore(sidebar, document.body.firstChild);
    }

    // 2. Mobile overlay (для закрытия sidebar тапом по тёмной зоне)
    let overlay = document.getElementById('crm-sidebar-mobile-overlay');
    if (!overlay) {
        overlay = document.createElement('div');
        overlay.id = 'crm-sidebar-mobile-overlay';
        overlay.addEventListener('click', () => {
            document.body.classList.remove('sidebar-mobile-open');
        });
        document.body.appendChild(overlay);
    }

    // 3. Top bar
    let topbar = document.getElementById('crm-topbar');
    if (!topbar) {
        topbar = document.createElement('header');
        topbar.id = 'crm-topbar';
        topbar.innerHTML = renderTopbar();
        document.body.insertBefore(topbar, sidebar.nextSibling);
    }

    // 4. Section content контейнер для non-calculator routes
    let sectionContent = document.getElementById('crm-section-content');
    if (!sectionContent) {
        sectionContent = document.createElement('main');
        sectionContent.id = 'crm-section-content';
        sectionContent.hidden = true;
        document.body.appendChild(sectionContent);
    }

    // 5. Sprint 23 W6+: floating help widget (?) — links to support.magellania.net
    try { mountHelpWidget(); } catch (e) { console.warn('[shell] mountHelpWidget failed:', e); }
}

// ─── User loading ────────────────────────────────────────────────────────────
// Sprint 15 C.1: JWT теперь httpOnly cookie — JS не может его декодировать.
// Тянем user-info через GET /api/v1/auth/me (cookie auth прозрачно отправляется).
//
// Sprint 20w: ИСПРАВЛЕН gateway auth bug — раньше использовали raw fetch
// без auto-refresh. Если access_token expired (>30 min idle) → /auth/me
// возвращал 401 → null → state.user=null → broken sidebar (fallback "[user]",
// missing admin items). fetchAuth прозрачно делает refresh + retry, и если
// refresh реально невозможен — редиректит на /login.html.
async function loadUserFromAPI() {
    // Sprint 20w perf: prefer /bootstrap (1 round-trip → user + currencies +
    // catalogs + suppliers prefilled). Fallback на /auth/me если bootstrap fail.
    if (window.apiClient && typeof window.apiClient.bootstrap === 'function') {
        try {
            const data = await window.apiClient.bootstrap();
            const u = data?.user;
            if (u) {
                // Сохраняем системные флаги в window для дальнейшего использования
                if (data.system_flags) window.__systemFlags = data.system_flags;
                return {
                    id: u.id,
                    email: u.email,
                    full_name: u.full_name || null,
                    username: u.username || null,
                    organization_id: u.organization_id,
                    organization_name: u.organization_name || null,
                    role: u.role,
                    is_management: u.is_management ? 1 : 0,
                    is_sandbox: u.is_sandbox ? 1 : 0
                };
            }
        } catch (e) {
            console.warn('[CRM Shell] bootstrap fail, falling back to /me:', e.message);
        }
    }
    // Fallback: legacy /auth/me
    try {
        const r = await fetchAuth('/api/v1/auth/me');
        if (!r.ok) {
            console.warn('[CRM Shell] /auth/me returned', r.status);
            return null;
        }
        const body = await r.json();
        const u = body?.data?.user;
        if (!u) return null;
        return {
            id: u.id,
            email: u.email,
            full_name: u.full_name || null,
            username: u.username || null,
            organization_id: u.organization_id,
            organization_name: u.organization_name || null,
            role: u.role,
            is_management: u.is_management ? 1 : 0
        };
    } catch (e) {
        console.warn('[CRM Shell] Failed to load /me:', e);
        return null;
    }
}

// ─── Hash router ────────────────────────────────────────────────────────────

/**
 * Парсит location.hash → { route, params }
 *   #/calculator         → { route: 'calculator', params: {} }
 *   #/calculator?id=abc  → { route: 'calculator', params: { id: 'abc' } }
 *   #/clients/123        → { route: 'clients', params: {}, sub: '123' }
 */
function parseHash() {
    const raw = location.hash.replace(/^#\/?/, '');
    const [pathPart, queryPart] = raw.split('?');
    const segments = pathPart.split('/').filter(Boolean);
    const route = segments[0] || '';
    const sub = segments[1] || null;

    const params = {};
    if (queryPart) {
        for (const pair of queryPart.split('&')) {
            const [k, v] = pair.split('=');
            if (k) params[decodeURIComponent(k)] = v == null ? '' : decodeURIComponent(v);
        }
    }
    return {
        route: ROUTES.find(r => r.path === route) ? route : DEFAULT_ROUTE,
        sub,
        params
    };
}

function getCurrentRoute() {
    return parseHash().route;
}

function setActiveRoute(routePath, params = {}, sub = null) {
    // Sprint 18: track previous route for "Back" button in calculator.
    // Не перезаписываем если переходим внутри того же route (id=X → id=Y).
    if (state.currentRoute && state.currentRoute !== routePath) {
        state.previousRoute = state.currentRoute;
        // Sprint 19e: запомнить ПОЛНЫЙ hash прежнего route (с query),
        // чтобы при back восстановить ?client=X / другие state-параметры.
        state.previousFullHash = state.currentFullHash || ('#/' + state.currentRoute);
    }
    state.currentRoute = routePath;
    state.params = params;
    state.subRoute = sub;  // Sprint 19c: deep-link sub-id (например /#/tours/:id)
    state.currentFullHash = location.hash;

    // body class for CSS rules
    document.body.classList.forEach(c => {
        if (c.startsWith('crm-route-')) document.body.classList.remove(c);
    });
    document.body.classList.add(`crm-route-${routePath}`);

    // sidebar active state
    document.querySelectorAll('.crm-nav-item').forEach(a => {
        a.classList.toggle('active', a.dataset.route === routePath);
    });

    // top bar route label
    const route = ROUTES.find(r => r.path === routePath);
    const topbarRoute = document.getElementById('crm-topbar-route');
    if (topbarRoute && route) {
        // Sprint 18 UI/UX: route.icon теперь Lucide name → render как <i data-lucide>
        // Sprint 24: prefer i18n nav.<key> with fallback to legacy route.label
        const routeLabel = route.i18nKey ? _t(`nav.${route.i18nKey}`, route.label) : route.label;
        topbarRoute.innerHTML = `<i data-lucide="${route.icon}" style="width:18px;height:18px;display:inline-block;vertical-align:-3px;margin-right:8px;"></i>${routeLabel}`;
        if (window.lucide && typeof window.lucide.createIcons === 'function') {
            try { window.lucide.createIcons(); } catch (_) {}
        }
    }

    // Render section content
    const sectionContent = document.getElementById('crm-section-content');
    if (routePath === 'calculator') {
        // Hide section content; show calculator (existing global-header + .app)
        if (sectionContent) sectionContent.hidden = true;

        // Sprint 18 lazy-load: триггерим Quill только при первом входе на /calculator.
        // initQuillEditors() — async + idempotent (skip если уже инициализированы).
        // Экономия: 48KB gzip + ~1.3s cold-load на routes без редактора.
        if (window.QuoteCalc?.initQuillEditors && !window.QuoteCalc.quillEditors) {
            Promise.resolve(window.QuoteCalc.initQuillEditors()).catch(err => {
                console.warn('[CRM Shell] Quill lazy-init failed (non-fatal):', err);
            });
        }

        // Sprint 18: wire "Back" button in calculator header (idempotent — replace listener)
        const backBtn = document.getElementById('calculator-back-btn');
        if (backBtn && !backBtn._wired) {
            backBtn._wired = true;
            backBtn.addEventListener('click', () => {
                // Sprint 19e: восстанавливаем ПОЛНЫЙ предыдущий hash (с query),
                // чтобы card view клиента не "схлопывался" при возврате.
                if (state.previousFullHash && !state.previousFullHash.startsWith('#/calculator')) {
                    location.hash = state.previousFullHash;
                    return;
                }
                const prev = state.previousRoute && state.previousRoute !== 'calculator'
                    ? state.previousRoute
                    : DEFAULT_ROUTE;
                location.hash = '#/' + prev;
            });
        }

        // 🔄 Query params: ?id=X → загрузить смету; ?new=1 → reset
        if (params.id && window.QuoteCalc?.loadQuoteFromServer) {
            const id = params.id;
            // Не перезагружаем если уже загружена та же
            if (window.QuoteCalc.state?.currentQuoteId !== id) {
                console.log('[CRM Shell] Loading estimate from query param:', id);
                Promise.resolve(window.QuoteCalc.loadQuoteFromServer(id)).catch(err => {
                    console.error('[CRM Shell] Failed to load estimate:', err);
                });
            }
        } else if (params.new === '1' && window.QuoteCalc?.createNewQuote) {
            console.log('[CRM Shell] Creating new quote from query param');
            Promise.resolve(window.QuoteCalc.createNewQuote()).catch(err => {
                console.error('[CRM Shell] Failed to create new:', err);
            });
        }
    } else {
        // Sprint 19: clear estimate lifecycle bar when leaving calculator
        // (it lives in index.html outside CRM-shell DOM, persists across routes)
        const lifecycleBar = document.getElementById('estimate-lifecycle-bar-container');
        if (lifecycleBar) {
            lifecycleBar.innerHTML = '';
            lifecycleBar.style.display = 'none';
        }

        // Sprint 20: dispatch unmount lifecycle event before swapping content.
        // Sections с module-level state (timers, intervals, cached data) могут
        // подписаться на 'crm:section-will-unmount' и сбросить state.
        // detail.from = previous route, detail.to = next route.
        try {
            window.dispatchEvent(new CustomEvent('crm:section-will-unmount', {
                detail: { from: state.previousRoute || null, to: routePath }
            }));
        } catch (_) { /* CustomEvent unsupported → skip silently */ }

        // Show section content; hide calculator (via body.crm-route-X CSS rule)
        if (sectionContent) {
            sectionContent.hidden = false;
            const loader = SECTION_LOADERS[routePath];
            if (loader) {
                // Sprint 20: clear DOM перед загрузкой новой секции — чтобы flash
                // stale content не появлялся пока loader async fetch'ит данные.
                sectionContent.innerHTML = '';
                Promise.resolve(loader(state.subRoute)).then(html => {
                    if (typeof html === 'string') {
                        sectionContent.innerHTML = html;
                    } else if (html instanceof HTMLElement) {
                        sectionContent.innerHTML = '';
                        sectionContent.appendChild(html);
                    }
                    // Sprint 18 UI/UX: re-init Lucide для динамически добавленных иконок
                    if (window.lucide && typeof window.lucide.createIcons === 'function') {
                        try { window.lucide.createIcons(); } catch (_) {}
                    }
                    // Sprint 20: notify mount complete — for sections that need
                    // to bind global keyboard shortcuts or setup listeners.
                    try {
                        window.dispatchEvent(new CustomEvent('crm:section-did-mount', {
                            detail: { route: routePath, sub: state.subRoute }
                        }));
                    } catch (_) { /* skip */ }
                }).catch(err => {
                    console.error(`[CRM Shell] Failed to render ${routePath}:`, err);
                    const errLabel = _t('shell.error.section_load', 'Ошибка загрузки секции');
                    sectionContent.innerHTML = `<div style="padding: 40px; color: #ef4444;">${errLabel}: ${err.message}</div>`;
                });
            }
        }
    }

    // Close mobile sidebar after navigation
    document.body.classList.remove('sidebar-mobile-open');
}

function handleHashChange() {
    const parsed = parseHash();
    setActiveRoute(parsed.route, parsed.params, parsed.sub);
}

// ─── Sidebar toggle ─────────────────────────────────────────────────────────
function bindSidebarToggle() {
    const toggle = document.getElementById('crm-sidebar-toggle');
    if (toggle) {
        toggle.addEventListener('click', () => {
            // На mobile (≤768px) toggle открывает/закрывает
            if (window.innerWidth <= 768) {
                document.body.classList.toggle('sidebar-mobile-open');
            } else {
                document.body.classList.toggle('sidebar-collapsed');
                try { localStorage.setItem('crm_sidebar_collapsed', document.body.classList.contains('sidebar-collapsed') ? '1' : '0'); } catch (_) {}
            }
        });
    }
    const burger = document.getElementById('crm-burger-btn');
    if (burger) {
        burger.addEventListener('click', () => {
            document.body.classList.toggle('sidebar-mobile-open');
        });
    }
    // Restore collapsed state from localStorage
    try {
        if (localStorage.getItem('crm_sidebar_collapsed') === '1') {
            document.body.classList.add('sidebar-collapsed');
        }
    } catch (_) {}
}

// ─── Logout ─────────────────────────────────────────────────────────────────
async function performLogout() {
    const ok = await confirmModal({
        title: _t('shell.logout.confirm_title', 'Выйти из системы?'),
        message: _t('shell.logout.confirm_message', 'Вы будете возвращены на страницу входа.'),
        confirmLabel: _t('nav.logout', 'Выйти'),
        cancelLabel: _t('btn.cancel', 'Отмена'),
        danger: true
    });
    if (!ok) return;
    try {
        await fetch('/api/v1/auth/logout', {
            method: 'POST',
            credentials: 'include'
        }).catch(() => {});
    } finally {
        try {
            localStorage.removeItem('jwt_token');
            localStorage.removeItem('jwt_refresh_token');
            localStorage.removeItem('authToken');
        } catch (_) {}
        location.replace('/login.html');
    }
}

function bindUserMenu() {
    // Sprint 20w: user-menu в sidebar — Профиль/Выйти + org switcher + sandbox controls.
    const trigger = document.getElementById('crm-sidebar-user-trigger');
    const menu = document.getElementById('crm-sidebar-user-menu');
    if (!trigger || !menu) return;

    let orgsLoaded = false;
    const loadOrgsLazy = async () => {
        if (orgsLoaded) return;
        orgsLoaded = true;
        try {
            const data = await loadMyOrgs();
            if (data && data.orgs) {
                renderUserMenuOrgs(data);
            } else {
                // Fallback: показать одну текущую org (для edge case superadmin)
                renderUserMenuOrgs({
                    current_organization_id: state.user?.organization_id,
                    orgs: state.user ? [{
                        organization_id: state.user.organization_id,
                        organization_name: state.user.organization_name || _t('shell.org_switcher.current_org', 'Текущая организация'),
                        role: state.user.role,
                        is_management: state.user.is_management ? 1 : 0,
                        is_current: true
                    }] : []
                });
            }
        } catch (err) {
            console.warn('[CRM Shell] loadMyOrgs failed:', err);
            const orgsContainer = document.getElementById('crm-user-menu-orgs');
            if (orgsContainer) {
                orgsContainer.innerHTML = `<div class="crm-user-menu-error">${_t('shell.org_switcher.load_failed', 'Не удалось загрузить список организаций.')}</div>`;
            }
        }
    };

    trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = menu.style.display === 'block';
        menu.style.display = isOpen ? 'none' : 'block';
        trigger.classList.toggle('open', !isOpen);
        if (!isOpen) loadOrgsLazy();  // lazy на первое открытие
    });

    document.addEventListener('click', (e) => {
        if (!trigger.contains(e.target) && !menu.contains(e.target)) {
            menu.style.display = 'none';
            trigger.classList.remove('open');
        }
    });

    menu.querySelectorAll('button[data-action]').forEach(btn => {
        btn.addEventListener('click', () => {
            const action = btn.dataset.action;
            menu.style.display = 'none';
            trigger.classList.remove('open');
            if (action === 'logout') performLogout();
            else if (action === 'goto-profile') location.hash = '#/profile';
            else if (action === 'open-docs') {
                // Sprint 24 W2: открыть документацию в новой вкладке
                const docsBase = (window.MAGELLANIA_DOCS_URL || 'https://support.magellania.net').replace(/\/$/, '');
                window.open(docsBase + '/', '_blank', 'noopener,noreferrer');
            }
        });
    });

    // Backward-compat: legacy logout button (если где-то остался)
    const legacyBtn = document.getElementById('crm-logout-btn');
    if (legacyBtn) legacyBtn.addEventListener('click', performLogout);
}

// Backward-compat alias — init.js может вызывать bindLogout
const bindLogout = bindUserMenu;

// ─── Org switcher (Sprint 20c) ──────────────────────────────────────────────
async function loadMyOrgs() {
    try {
        const res = await fetchAuth('/api/v1/auth/my-orgs');
        if (!res.ok) return null;
        const data = await res.json();
        return data.success ? data.data : null;
    } catch (_) {
        return null;
    }
}

function roleLabel(role) {
    const fallbacks = {
        owner: 'Владелец', admin: 'Администратор', user: 'Пользователь',
        sales_manager: 'Менеджер по продажам',
        superadmin: 'Суперадмин', crm_admin: 'CRM-админ',
        crm_sales_manager: 'CRM Sales Manager'
    };
    if (!role) return '';
    return _t(`shell.role.${role}`, fallbacks[role] || role);
}

/**
 * Sprint 20w UX: org-switcher + sandbox controls встроены в sidebar user-menu.
 * Рендерит секции #crm-user-menu-orgs + #crm-user-menu-sandbox внутри dropdown.
 * Стилизуется под dark sidebar (см. .crm-user-menu-section в crm-shell.css).
 */
function renderUserMenuOrgs(orgsData) {
    const orgsContainer = document.getElementById('crm-user-menu-orgs');
    const sandboxContainer = document.getElementById('crm-user-menu-sandbox');
    if (!orgsContainer || !sandboxContainer || !orgsData || !orgsData.orgs) return;

    // ── Orgs list ────────────────────────────────────────────────────────────
    const header = orgsData.orgs.length > 1
        ? `<div class="crm-user-menu-header">${_t('shell.org_switcher.orgs_count', 'Организации').replace('{n}', orgsData.orgs.length)} (${orgsData.orgs.length})</div>`
        : `<div class="crm-user-menu-header">${_t('shell.org_switcher.current_org', 'Текущая организация')}</div>`;

    const orgsHTML = orgsData.orgs.map(o => `
        <button type="button" class="crm-user-menu-org-item${o.is_current ? ' is-current' : ''}"
                data-org-id="${escapeHtml(o.organization_id)}">
            <span class="crm-user-menu-org-info">
                <span class="crm-user-menu-org-name">${escapeHtml(o.organization_name)}</span>
                <span class="crm-user-menu-org-role">${escapeHtml(roleLabel(o.role))}</span>
            </span>
            <span class="crm-user-menu-org-badges">
                ${o.is_current ? '<i data-lucide="check" style="width:14px;height:14px"></i>' : ''}
            </span>
        </button>
    `).join('');

    orgsContainer.innerHTML = header + orgsHTML;

    // ── Sandbox controls ─────────────────────────────────────────────────────
    const inSandbox = state.user?.is_sandbox === 1;
    const sandboxFeatureEnabled = state.user?.sandbox_enabled !== 0;  // default ON
    const allowedRolesForSandbox = ['owner', 'admin', 'superadmin', 'crm_admin'];
    const canUseSandbox = allowedRolesForSandbox.includes(state.user?.role) && sandboxFeatureEnabled;

    // Sprint 20w UX: если sandbox уже есть в списке orgs[] (user — member своего sandbox),
    // прячем кнопку "Открыть Sandbox" — пользователь просто кликает по item в списке.
    // Кнопка "Сбросить" остаётся (доступна только когда current = sandbox).
    const sandboxAlreadyExists = orgsData.orgs.some(o => /sandbox|песочница/i.test(o.organization_name || ''));

    if (canUseSandbox) {
        if (inSandbox) {
            sandboxContainer.innerHTML =
                `<button type="button" id="crm-sandbox-reset" class="crm-user-menu-sandbox-btn variant-reset">
                     <i data-lucide="refresh-cw" style="width:14px;height:14px"></i>
                     <span>${_t('shell.sandbox.reset_btn', 'Сбросить Sandbox')}</span>
                 </button>`;
        } else if (!sandboxAlreadyExists) {
            sandboxContainer.innerHTML =
                `<button type="button" id="crm-sandbox-toggle" class="crm-user-menu-sandbox-btn variant-open">
                     <span>${_t('shell.sandbox.open_btn', 'Открыть Sandbox')}</span>
                 </button>
                 <div class="crm-user-menu-sandbox-hint">${_t('shell.sandbox.hint', 'тестовые данные, можно потыкать')}</div>`;
        } else {
            sandboxContainer.innerHTML = '';  // sandbox уже есть в списке orgs — просто кликни
        }
    } else {
        sandboxContainer.innerHTML = '';
    }

    // ── Handlers ─────────────────────────────────────────────────────────────
    // Switch org
    orgsContainer.querySelectorAll('[data-org-id]').forEach(btn => {
        btn.addEventListener('click', async () => {
            const orgId = btn.dataset.orgId;
            const current = orgsData.orgs.find(o => o.is_current);
            if (current && current.organization_id === orgId) return;
            btn.style.opacity = '0.5';
            try {
                const res = await fetchAuth('/api/v1/auth/switch-org', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ organization_id: orgId })
                });
                if (res.ok) location.reload();
                else {
                    btn.style.opacity = '1';
                    await confirmModal({
                        title: _t('common.error', 'Ошибка'),
                        message: _t('shell.org_switcher.switch_failed', 'Не удалось переключиться.'),
                        confirmLabel: _t('btn.ok', 'OK'),
                        cancelLabel: _t('btn.cancel', 'Отмена')
                    });
                }
            } catch (err) {
                btn.style.opacity = '1';
                await confirmModal({
                    title: _t('common.error', 'Ошибка'),
                    message: err.message,
                    confirmLabel: _t('btn.ok', 'OK'),
                    cancelLabel: _t('btn.cancel', 'Отмена')
                });
            }
        });
    });

    // Sandbox: open
    const sandboxToggleBtn = sandboxContainer.querySelector('#crm-sandbox-toggle');
    if (sandboxToggleBtn) {
        sandboxToggleBtn.addEventListener('click', async () => {
            sandboxToggleBtn.disabled = true;
            sandboxToggleBtn.querySelector('span:last-child').textContent = _t('shell.sandbox.creating', '⏳ Создаём…');
            try {
                const initRes = await fetchAuth('/api/v1/sandbox/init', { method: 'POST' });
                const initData = await initRes.json();
                if (!initData.success) throw new Error(initData.error || 'init fail');
                await fetchAuth('/api/v1/auth/switch-org', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ organization_id: initData.data.sandbox.id })
                });
                location.reload();
            } catch (err) {
                await confirmModal({
                    title: _t('common.error', 'Ошибка'),
                    message: err.message,
                    confirmLabel: _t('btn.ok', 'OK'),
                    cancelLabel: _t('btn.cancel', 'Отмена')
                });
                sandboxToggleBtn.disabled = false;
                sandboxToggleBtn.querySelector('span:last-child').textContent = _t('shell.sandbox.open_btn', 'Открыть Sandbox');
            }
        });
    }

    // Sandbox: reset
    const sandboxResetBtn = sandboxContainer.querySelector('#crm-sandbox-reset');
    if (sandboxResetBtn) {
        sandboxResetBtn.addEventListener('click', async () => {
            const ok = await confirmModal({
                title: _t('shell.sandbox.reset_confirm_title', 'Сбросить Sandbox?'),
                message: _t('shell.sandbox.reset_confirm_message', 'Все изменения внутри sandbox будут потеряны.'),
                confirmLabel: _t('shell.sandbox.reset_btn', 'Сбросить Sandbox'),
                cancelLabel: _t('btn.cancel', 'Отмена'),
                danger: true
            });
            if (!ok) return;
            sandboxResetBtn.disabled = true;
            try {
                const r = await fetchAuth('/api/v1/sandbox/reset', { method: 'POST' });
                const data = await r.json();
                if (data.success) {
                    location.reload();
                } else {
                    await confirmModal({
                        title: _t('common.error', 'Ошибка'),
                        message: _t('shell.sandbox.reset_failed', 'Не удалось сбросить') + ': ' + (data.error || 'unknown'),
                        confirmLabel: _t('btn.ok', 'OK'),
                        cancelLabel: _t('btn.cancel', 'Отмена')
                    });
                }
            } catch (err) {
                await confirmModal({
                    title: _t('common.error', 'Ошибка'),
                    message: err.message,
                    confirmLabel: _t('btn.ok', 'OK'),
                    cancelLabel: _t('btn.cancel', 'Отмена')
                });
            }
        });
    }

    // Re-init Lucide для новых иконок (check, flask-conical, refresh-cw, log-in)
    if (window.lucide?.createIcons) {
        try { window.lucide.createIcons(); } catch (_) {}
    }
}

// ─── Init ───────────────────────────────────────────────────────────────────
// Sprint 15 C.1: async — нужно дождаться /me перед рендером (роль определяет UI).
export async function initCrmShell() {
    // Sprint 20w perf Phase 4.2: register Service Worker для repeat visits cache
    if ('serviceWorker' in navigator && location.protocol === 'https:') {
        navigator.serviceWorker.register('/sw.js', { scope: '/' })
            .then(() => { /* registered */ })
            .catch(err => console.warn('[CRM Shell] SW registration failed:', err.message));
    }

    state.user = await loadUserFromAPI();
    // B010 fix: expose user в window для app.js (per-org localStorage keys)
    if (state.user) {
        window.__crmUser__ = state.user;
        // B013 fix: pass orgId — migrate legacy regions ВМЕСТО drop
        cleanupLegacyStorage(state.user.organization_id);
        // Sprint 21 W1 / 23 W4: apply ui_density preference на html element
        // (sync match с early-auth-guard.js которая использует localStorage).
        const density = state.user.ui_density || 'cozy';
        document.documentElement.classList.remove('density-compact', 'density-cozy', 'density-comfortable');
        document.documentElement.classList.add('density-' + density);
        try { localStorage.setItem('user.ui_density', density); } catch (_) {}

        // Sprint 22 W2 / 23 W4: theme apply на html + localStorage кэш.
        // Default → 'light' (раньше было 'auto'). Pure system-preference опция
        // остаётся доступной opt-in для пользователя.
        const theme = state.user.theme || 'light';
        try { localStorage.setItem('user.theme', theme); } catch (_) {}
        document.documentElement.classList.remove('theme-light', 'theme-dark');
        if (theme === 'dark') {
            document.documentElement.classList.add('theme-dark');
        } else if (theme === 'auto') {
            const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            document.documentElement.classList.add(prefersDark ? 'theme-dark' : 'theme-light');
        } else {
            document.documentElement.classList.add('theme-light');
        }

        // Sprint 21 W4: locale priority:
        //   1. localStorage (manual override) — наивысший
        //   2. user.locale (sets в Profile)
        //   3. organization.default_language (org-level default)
        //   4. navigator.language (fallback)
        let initialLocale = null;
        try { initialLocale = localStorage.getItem('locale'); } catch (_) {}
        if (!initialLocale) initialLocale = state.user.locale || state.user.preferred_language;
        if (!initialLocale && state.organization?.default_language) {
            initialLocale = state.organization.default_language;
        }
        // setLocale handles unsupported → fallback to 'ru'
        try { await setLocale(initialLocale || 'ru'); } catch (e) { console.warn('[i18n] init failed:', e); }
    }

    injectShellDOM();
    document.body.classList.add('crm-shell-active');

    // Sprint 18 UI/UX: init Lucide icons после инжекта sidebar/topbar
    if (window.lucide && typeof window.lucide.createIcons === 'function') {
        try { window.lucide.createIcons(); }
        catch (err) { console.warn('[CRM Shell] lucide.createIcons failed:', err); }
    }

    bindSidebarToggle();
    bindUserMenu();  // Sprint 20w UX: orgs + sandbox + profile/logout — всё в одном dropdown

    window.addEventListener('hashchange', handleHashChange);

    // Sprint 20m: re-render sidebar when locale changes (i18n module dispatches
    // 'i18n:locale-changed' on document). Pure innerHTML swap — preserves
    // body classes and topbar.
    //
    // Sprint 24 fix: innerHTML swap уничтожает event listeners на любых
    // child-нодах. Нужно ре-bind'ить ВСЕ sidebar-bound handlers, иначе
    // user-dropdown / nav-clicks / sandbox-controls перестают работать.
    document.addEventListener('i18n:locale-changed', () => {
        const sidebar = document.getElementById('crm-sidebar');
        if (sidebar) {
            sidebar.innerHTML = renderSidebar();
            // Re-bind sidebar-specific events
            bindSidebarToggle();
            bindUserMenu();  // ← Sprint 24 fix: dropdown + org switcher + sandbox + profile/logout
            // Re-init icons (Lucide replaces <i data-lucide> with <svg>)
            if (window.lucide?.createIcons) {
                try { window.lucide.createIcons(); } catch (_) {}
            }
            // Re-apply active state for current route
            document.querySelectorAll('.crm-nav-item').forEach(a => {
                a.classList.toggle('active', a.dataset.route === state.currentRoute);
            });
        }
    });

    // Если hash пустой — переход на default route
    if (!location.hash || location.hash === '#' || location.hash === '#/') {
        location.replace('#/' + DEFAULT_ROUTE);
        return; // hashchange сам сработает
    }

    const initial = parseHash();
    setActiveRoute(initial.route, initial.params, initial.sub);
    console.log('[CRM Shell] Initialized — user:', state.user?.email, 'route:', state.currentRoute);
}

// Export for debugging
if (typeof window !== 'undefined') {
    window.CrmShell = { initCrmShell, getCurrentRoute, setActiveRoute, ROUTES };
}
